import { CourseCustomStyle, CourseCustomStyleHttpResponse } from 'app/types/courseCustomStyle';
import { getCourseCustomStyle } from 'core/crud/templates.crud';
import { createCachedProgramResource } from 'core/lite/hooks/createCachedProgramResource';
import { normalizeCssColor } from 'core/utils/ui';

type BrandColors = {
  primary?: string;
  secondary?: string;
};

export type UseProgramBrandColorsResult = {
  brandColors: BrandColors | null;
  isReady: boolean;
};

// El motor del visor solo parsea `#rgb` y `#rrggbb`: cualquier otra notación rompe la derivación de tintes.
const SUPPORTED_HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function toBrandColor(value: string | null | undefined): string | undefined {
  // El BO admite el hex sin almohadilla y el visor solo entiende CSS válido.
  const normalized = normalizeCssColor(value) || undefined;
  if (!normalized || !SUPPORTED_HEX.test(normalized)) return undefined;

  return normalized;
}

function toBrandColors(style: CourseCustomStyle | undefined): BrandColors | null {
  const primary = toBrandColor(style?.color_first);
  const secondary = toBrandColor(style?.color_second);

  if (!primary && !secondary) return null;

  const brandColors: BrandColors = {};
  if (primary) brandColors.primary = primary;
  if (secondary) brandColors.secondary = secondary;

  return brandColors;
}

const { useResource, clearCache } = createCachedProgramResource<BrandColors | null>(async (programGuid) => {
  const response = (await getCourseCustomStyle(programGuid)) as CourseCustomStyleHttpResponse;
  const httpStatus = response?.status ?? response?.error?.status;

  if (httpStatus === 200) return { value: toBrandColors(response?.data?.[0]), isCacheable: true };

  // El 404 "este programa no tiene Apariencia" es determinista; el resto se reintenta al siguiente montaje.
  return { value: null, isCacheable: httpStatus === 404 };
}, null);

// Solo para tests: aísla casos; en runtime no se invoca.
export const __clearProgramBrandColorsCache = clearCache;

export default function useProgramBrandColors(programGuid: string | undefined): UseProgramBrandColorsResult {
  const { value, isReady } = useResource(programGuid);

  return { brandColors: value, isReady };
}
